import * as React from 'react'
import { ReactElement, useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Button } from '@fluentui/react-components'
import { SearchRegular } from '@fluentui/react-icons'
import { LegalPersonMutationDto } from '../../../config/partyMutationConfig'
import { ListItemDto } from '../../../../config/partyConfig'
import useAxios from '../../../../hooks/useAxios'
import FieldRow from './FieldRow'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import LegalStructureCombobox, { LegalStructureComboboxProps } from '../../../../components/Combobox/i18n/LegalStructureCombobox'
import KvkSearchDialog from '../../../../components/Dialog/ChamberOfCommerce/KvkSearchDialog'

/**
 * Name & registration details of a legal person, with the KvK (Chamber of Commerce) search that
 * pre-fills the fields and warns on a duplicate CoC (+ branch) combination.
 */
export default function LegalNameSection(): ReactElement {
    const { t: translate } = useTranslation()
    const axios = useAxios()
    const { values, setValues, setErrors } = useFormikContext<LegalPersonMutationDto>()
    const [kvkHidden, setKvkHidden] = useState(true)

    return (
        <>
            <Button appearance="primary" icon={<SearchRegular />} onClick={() => setKvkHidden(false)}>
                {translate('party.edit.coc.search.title')}
            </Button>
            <KvkSearchDialog
                hidden={kvkHidden}
                onDismiss={() => setKvkHidden(true)}
                onSelectCompany={company => {
                    setValues({ ...values, ...company })
                    axios
                        .get<ListItemDto>(`/party/search/${company.cocNumber}`, { params: { cocBranchNumber: company.cocBranchNumber } })
                        .then(result => {
                            if (result?.data?.id) {
                                setErrors({
                                    cocNumber: translate(
                                        company.cocBranchNumber ? 'party.edit.coc.combination.error' : 'party.edit.coc.cocNumber.error',
                                    ),
                                })
                            }
                        })
                }}
            />
            <FormikTextareaField<LegalPersonMutationDto>
                required
                labelKey="party.edit.company.name"
                fieldName="name"
                componentProps={{ rows: 2, autoFocus: true }}
            />
            <FormikInputField<LegalPersonMutationDto> required={values.crmRelation} labelKey="party.edit.company.shortname" fieldName="shortName" />
            <FieldRow>
                <FormikComboboxField<LegalPersonMutationDto, LegalStructureComboboxProps>
                    labelKey="party.edit.company.legalstructure"
                    fieldName="legalStructure"
                    component={LegalStructureCombobox}
                    componentProps={{ clearable: false }}
                />
                <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.company.cocNumber" fieldName="cocNumber" />
                <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.company.cocBranchNumber" fieldName="cocBranchNumber" />
            </FieldRow>
        </>
    )
}
